import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import MentionAutocomplete from './MentionAutocomplete';
import AgentResponseEditor from './AgentResponseEditor';
import GraphComponent from '../copilotComponents/GraphComponent'
import TableComponent from '../copilotComponents/TableComponent';
import ReferencesComponent from '../copilotComponents/ReferencesComponent';
import ImageComponent from '../copilotComponents/ImageComponent';
import InlineArtifactRenderer from './InlineArtifactRenderer';
import { ColorPickerButton } from './ReportFormattingModal';
import RichFormatPill from './RichFormatPill';
import AgentTranslationModal from './AgentTranslationModal';
import AgentLogModal from './AgentLogModal';
import AgentWarningsModal from './AgentWarningsModal.jsx';
import { useTimezone } from '../../utils/TimezoneContext';

// card6/card7 are doc_id/ar_id citations; card9/card10 are the display-only section and
// figure/table cross-reference chips. All four are tokenized out of the edit buffer so a
// user cannot edit the text inside one. handleEditClick loads markdown_raw (tokens, no
// chips) when it is present, so this is the second line of defence before saving.
const CITATION_SPAN_REGEX =
  /<span\s+[^>]*class=(?:"[^"]*\b(?:ev-message-card6|ev-message-card7|ev-message-card9|ev-message-card10)\b[^"]*"|'[^']*\b(?:ev-message-card6|ev-message-card7|ev-message-card9|ev-message-card10)\b[^']*')[^>]*>[\s\S]*?<\/span>/g;
const CITATION_TOKEN_REGEX = /\u27E6CITE:(c\d+)\u27E7/g;

const SYNTHETIC_SPACE_MARK = '\u200D';

const tokenizeCitations = (markdown, existingCitationMap = {}, initialCounter = 0) => {
  if (!markdown) {
    return {
      tokenizedMarkdown: markdown || '',
      citationMap: { ...existingCitationMap },
      counter: initialCounter,
    };
  }
  const citationMap = { ...existingCitationMap };
  let counter = initialCounter;

  const tokenizedMarkdown = markdown.replace(
    CITATION_SPAN_REGEX,
    (match, offset) => {
      counter += 1;
      const tokenId = `c${counter}`;
      citationMap[tokenId] = match;
      const prevChar = offset > 0 ? markdown.charAt(offset - 1) : '';
      const lead = prevChar && !/\s/.test(prevChar) ? ` ${SYNTHETIC_SPACE_MARK}` : '';
      return `${lead}\u27E6CITE:${tokenId}\u27E7`;
    }
  );
  return { tokenizedMarkdown, citationMap, counter };
};

const restoreCitations = (tokenizedMarkdown, citationMap) => {
  if (!tokenizedMarkdown) return tokenizedMarkdown || '';
  const withSpans = tokenizedMarkdown.replace(CITATION_TOKEN_REGEX, (match, tokenId) => {
    return citationMap[tokenId] ?? '';
  });
  return withSpans
    .replace(new RegExp(` ${SYNTHETIC_SPACE_MARK}`, 'g'), '')
    .replace(new RegExp(SYNTHETIC_SPACE_MARK, 'g'), '');
};

const GenbiResponseComponent = ({ agent, status, onTranslate, isTranslateDisabled, fabricId, authToken, headerExtra, responseHeaderNotice, onSaveResponse, referenceIndex, compact = false, formatJson = null, agentDocs = [], templateId = null }) => {

  const { formatDateTime } = useTimezone();
  const markdownComponents = {
    h1: ({ children }) => <div className="ev-med-p-blue mb-2">{children}</div>,
    h2: ({ children }) => <div className="ev-reg-p-dark mb-1 fw-bold">{children}</div>,
    h3: ({ children }) => <div className="ev-reg-p-dark mb-1 fw-bold">{children}</div>,
    h4: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
    h5: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
    h6: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
  };

  const shouldRenderFullResponse = status === 'COMPLETED' && agent.response;
  const promptLangId = agent.context_lang_id ?? 1;
  const responseLangId = agent.response?.language_id ?? 1;
  const hasLangMismatch = status === 'COMPLETED' && promptLangId !== responseLangId;
  const [isTranslatePopupOpen, setIsTranslatePopupOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [isWarningsModalOpen, setIsWarningsModalOpen] = useState(false);
  const latestVersion = agent?.versions?.[0]?.version ?? 1;

  // Review flags stamped by the backend during post-generation scans.
  const sensitiveFlags = agent?.response?.execution_metadata?.sensitive_output_flags ?? [];
  const groundingFlags = agent?.response?.execution_metadata?.grounding_flags ?? [];
  const boundsEnforcement = agent?.response?.execution_metadata?.bounds_enforcement;
  const retrievalConfidence = agent?.response?.execution_metadata?.retrieval_confidence;
  const hasBoundsIssues =
    (boundsEnforcement?.graphs?.dropped ?? 0) > 0 ||
    (boundsEnforcement?.tables?.dropped ?? 0) > 0 ||
    (boundsEnforcement?.images?.dropped ?? 0) > 0 ||
    boundsEnforcement?.markdown?.truncated === true;
  const hasWarnings = sensitiveFlags.length > 0 || groundingFlags.length > 0 || hasBoundsIssues;

  const [isEditing, setIsEditing] = useState(false);
  const [editedMarkdown, setEditedMarkdown] = useState('');
  const [editedTables, setEditedTables] = useState({});
  // Tokenized baseline of tables captured at edit-start; used to send only changed tables on save.
  const originalTablesRef = useRef({});
  const [citationMap, setCitationMap] = useState({});
  const [editedGraphs, setEditedGraphs] = useState({});
  const [editedImages, setEditedImages] = useState({});
  // Edits to in-place graphs (those embedded inside table cells).
  // { [graph_id]: { subtitle?, title? } }
  const [editedInPlaceGraphs, setEditedInPlaceGraphs] = useState({});
  // Items deleted during the current edit session: { kind: 'table'|'graph'|'image'|'ipimage', id, label }.
  // Drives the delete payload and the live "still referenced" warning banner.
  const [deletedItems, setDeletedItems] = useState([]);
  const hasPendingEdits = !!agent?.editedResponse;
  const isManualResponse = agent?.response?.execution_metadata?.manual_edit === true;

  const [inPlaceLightbox, setInPlaceLightbox] = useState(null);
  const [viewingInPlaceGraph, setViewingInPlaceGraph] = useState(null);
  const [editingInPlaceGraph, setEditingInPlaceGraph] = useState(null);
  const [editingInPlaceImage, setEditingInPlaceImage] = useState(null);
  const [editedInPlaceImages, setEditedInPlaceImages] = useState({});

  const ipGraphSubtitleRef = useRef(null);
  const ipGraphTitleRef = useRef(null);
  const ipImageSubtitleRef = useRef(null);
  const ipImageFooterRef = useRef(null);

  const [agentRefs, setAgentRefs] = useState([]);
  const refsLoadedRef = useRef(false);
  const [refsLoading, setRefsLoading] = useState(false);

  const fetchAvailableReferences = useCallback(async () => {
    if (refsLoadedRef.current || refsLoading) return;
    setRefsLoading(true);
    try {
      const res = await axios.post(
        '/pceapi/genbi/get-agent-available-references',
        { agent_id: agent.id, fabric_id: fabricId },
        { headers: { Authorization: authToken || GetCookie('access_token') } }
      );
      if (res?.data?.status === 'success' && Array.isArray(res.data.data)) {
        setAgentRefs(res.data.data
          .filter(r => r && r.ar_id != null)
          .map(r => ({
            ar_id: r.ar_id, authors: r.authors || [], year: r.year, title: r.title,
            is_manual: !!r.is_manual,
            doc_id: r.doc_id != null ? r.doc_id : null,
            doc_name: r.doc_name || null,
          })));
        refsLoadedRef.current = true;
      }
    } catch (e) {
      // Non-fatal: References tab just stays empty if this fails.
      console.error('Failed to fetch available references', e);
    } finally {
      setRefsLoading(false);
    }
  }, [agent.id, fabricId, authToken, refsLoading]);

  const createManualReference = useCallback(async (fields) => {
    try {
      const res = await axios.post(
        '/pceapi/genbi/create-manual-reference',
        { template_id: templateId, fabric_id: fabricId, ...fields },
        { headers: { Authorization: authToken || GetCookie('access_token') } }
      );
      if (res?.data?.status === 'success' && res.data.data?.ar_id != null) {
        const created = {
          ar_id: res.data.data.ar_id,
          authors: res.data.data.authors || [],
          year: res.data.data.year,
          title: res.data.data.title,
          is_manual: true,
          doc_id: null,
          doc_name: null,
        };
        setAgentRefs(prev => {
          if (prev.some(r => r.ar_id === created.ar_id)) return prev;
          return [created, ...prev];
        });
        refsLoadedRef.current = true;
        return created;
      }
      return null;
    } catch (e) {
      console.error('Failed to create manual reference', e);
      throw e;
    }
  }, [templateId, fabricId, authToken]);

  const handleTranslateClick = () => {
    setIsTranslatePopupOpen(true);
  };

  const handleCloseTranslatePopup = () => {
    setIsTranslatePopupOpen(false);
  };

  const handleTranslate = (targetLangId) => {
    setIsTranslatePopupOpen(false);
    if (onTranslate) {
      onTranslate(agent.id, targetLangId);
    }
  };

  const handleEditClick = useCallback(() => {
    const rawMarkdown =
      agent?.editedResponse?.markdown ??
      agent?.response?.markdown_raw ??
      agent?.response?.markdown ??
      '';
    setEditedMarkdown(rawMarkdown);

    let nextCitationMap = {};
    let nextCitationCounter = 0;

    const tokenizeEditField = (value) => {
      const result = tokenizeCitations(
        value == null ? '' : String(value),
        nextCitationMap,
        nextCitationCounter
      );
      nextCitationMap = result.citationMap;
      nextCitationCounter = result.counter;
      return result.tokenizedMarkdown;
    };

    const pendingTablesById = (agent?.editedResponse?.tables || []).reduce((acc, t) => {
      if (t?.table_id) acc[t.table_id] = { html: t.html, footnote: t.footnote };
      return acc;
    }, {});

    const existingTablesMap = (agent?.response?.tables || []).reduce((acc, table) => {
      if (!table?.table_id) return acc;
      const pending = pendingTablesById[table.table_id] || {};
      const html = pending.html ?? table.html ?? '';
      const footnote = pending.footnote ?? table.footnote;
      const fields = {};
      if (html != null) fields.html = tokenizeEditField(html);
      if (footnote != null) fields.footnote = tokenizeEditField(footnote);
      acc[table.table_id] = fields;
      return acc;
    }, {});
    setEditedTables(existingTablesMap);
    originalTablesRef.current = { ...existingTablesMap };

    const pendingGraphsById = (agent?.editedResponse?.graphs || []).reduce((acc, g) => {
      if (g?.graph_id) acc[g.graph_id] = g;
      return acc;
    }, {});

    const getGraphTitle = (graph) => {
      const layoutTitle = graph?.figure_dict?.layout?.title;
      if (typeof layoutTitle === 'string') return layoutTitle;
      return layoutTitle?.text;
    };

    const existingGraphsMap = (agent?.response?.graphs || []).reduce((acc, graph) => {
      if (!graph?.graph_id) return acc;
      const pendingGraph = pendingGraphsById[graph.graph_id] || {};
      const subtitle = pendingGraph.subtitle ?? graph.subtitle;
      const title = pendingGraph.title ?? graph.title ?? getGraphTitle(graph);
      const fields = {};
      if (subtitle != null) fields.subtitle = tokenizeEditField(subtitle);
      if (title != null) fields.title = tokenizeEditField(title);
      if (Object.keys(fields).length > 0) acc[graph.graph_id] = fields;
      return acc;
    }, {});
    setEditedGraphs(existingGraphsMap);

    const pendingImagesById = (agent?.editedResponse?.images || []).reduce((acc, img) => {
      if (img?.image_id) acc[img.image_id] = img;
      return acc;
    }, {});

    const existingImagesMap = (agent?.response?.images || []).reduce((acc, img) => {
      if (!img?.image_id) return acc;
      const pendingImage = pendingImagesById[img.image_id] || {};
      const subtitle = pendingImage.subtitle ?? img.subtitle;
      const footer = pendingImage.footer ?? img.footer;
      const fields = {};
      if (subtitle != null) fields.subtitle = tokenizeEditField(subtitle);
      if (footer != null) fields.footer = tokenizeEditField(footer);
      if (Object.keys(fields).length > 0) acc[img.image_id] = fields;
      return acc;
    }, {});
    setEditedImages(existingImagesMap);
    setCitationMap(nextCitationMap);

    const er = agent?.editedResponse || {};
    const numById = (list, idKey, numKeys) => {
      const m = {};
      (list || []).forEach((item, i) => {
        const id = item?.[idKey];
        if (!id) return;
        let num;
        for (const k of numKeys) { if (item?.[k] != null) { num = item[k]; break; } }
        m[id] = num ?? (i + 1);
      });
      return m;
    };
    const tableNumById = numById(agent?.response?.tables, 'table_id', ['table_number']);
    const graphNumById = {
      ...numById(agent?.response?.graphs, 'graph_id', ['graph_number']),
      ...numById(agent?.response?.in_place_graphs, 'graph_id', ['img_num', 'index']),
    };
    const imageNumById = numById(agent?.response?.images, 'image_id', ['img_num']);
    const ipiNumById = numById(agent?.response?.in_place_images, 'in_place_image_id', ['img_num', 'index']);
    const ipgNumById = numById(agent?.response?.in_place_graphs, 'in_place_graph_id', ['img_num', 'index']);
    const label = (prefix, n) => (n != null && n !== '' ? `${prefix} ${n}` : prefix);

    const seededDeleted = [
      ...(er.deleted_table_ids || []).map(id => ({ kind: 'table', id, label: label('Table', tableNumById[id]) })),
      ...(er.deleted_graph_ids || []).map(id => ({ kind: 'graph', id, label: label('Figure', graphNumById[id]) })),
      ...(er.deleted_image_ids || []).map(id => ({ kind: 'image', id, label: label('Figure', imageNumById[id]) })),
      ...(er.deleted_in_place_image_ids || []).map(id => ({ kind: 'ipimage', id, label: label('In-place Figure', ipiNumById[id]) })),
      ...(er.deleted_in_place_graph_ids || []).map(id => ({ kind: 'ipgraph', id, label: label('In-place Figure', ipgNumById[id]) })),
    ];

    setDeletedItems(seededDeleted);

    const inPlaceGraphIds = new Set(
      (agent?.response?.in_place_graphs || []).map(g => g?.graph_id).filter(Boolean)
    );
    const seededIpGraphs = {};
    (er.graphs || []).forEach(g => {
      if (g?.graph_id && inPlaceGraphIds.has(g.graph_id)) {
        seededIpGraphs[g.graph_id] = {
          ...('subtitle' in g && { subtitle: g.subtitle }),
          ...('title' in g && { title: g.title }),
        };
      }
    });
    const seededIpImages = {};
    (er.in_place_images || []).forEach(ipi => {
      if (ipi?.in_place_image_id) {
        seededIpImages[ipi.in_place_image_id] = {
          ...('subtitle' in ipi && { subtitle: ipi.subtitle }),
          ...('footer' in ipi && { footer: ipi.footer }),
        };
      }
    });
    setEditedInPlaceGraphs(seededIpGraphs);
    setEditedInPlaceImages(seededIpImages);

    setIsEditing(true);
  }, [agent?.editedResponse, agent?.response?.graphs, agent?.response?.images, agent?.response?.markdown, agent?.response?.tables, agent?.response?.in_place_graphs, agent?.response?.in_place_images]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedMarkdown('');
    setEditedTables({});
    setEditedGraphs({});
    setEditedImages({});
    setEditedInPlaceGraphs({});
    setEditedInPlaceImages({});
    setDeletedItems([]);
    setCitationMap({});
  }, []);

  const handleTableFieldChange = useCallback((tableId, field, value) => {
    setEditedTables(prev => ({
      ...prev,
      [tableId]: { ...prev[tableId], [field]: value }
    }));
  }, []);

  const handleGraphFieldChange = useCallback((graphId, field, value) => {
    setEditedGraphs(prev => ({
      ...prev,
      [graphId]: { ...prev[graphId], [field]: value }
    }));
  }, []);

  const handleImageFieldChange = useCallback((imageId, field, value) => {
    setEditedImages(prev => ({
      ...prev,
      [imageId]: { ...prev[imageId], [field]: value }
    }));
  }, []);

  const handleInPlaceGraphChange = useCallback((graphId, changes) => {
    setEditedInPlaceGraphs(prev => ({
      ...prev,
      [graphId]: { ...prev[graphId], ...changes }
    }));
  }, []);

  const handleInPlaceImageChange = useCallback((imageId, changes) => {
    setEditedInPlaceImages(prev => ({
      ...prev,
      [imageId]: { ...prev[imageId], ...changes }
    }));
  }, []);

  const handleDeleteInPlaceImage = useCallback((ipiId) => {
    if (!ipiId) return;
    setDeletedItems(prev =>
      prev.some(d => d.id === ipiId) ? prev : [...prev, { kind: 'ipimage', id: ipiId, label: 'In-place Figure' }]
    );
    setEditedInPlaceImages(prev => { const n = { ...prev }; delete n[ipiId]; return n; });
  }, []);

  const handleDeleteInPlaceGraph = useCallback((ipgId) => {
    if (!ipgId) return;
    setDeletedItems(prev =>
      prev.some(d => d.id === ipgId) ? prev : [...prev, { kind: 'ipgraph', id: ipgId, label: `In-place Figure` }]
    );
    setEditedInPlaceGraphs(prev => { const n = { ...prev }; delete n[ipgId]; return n; });
  }, []);

  // Delete a table/figure from the response (edit mode only)
  const handleDeleteItem = useCallback((kind, id, label) => {
    if (!id) return;
    setDeletedItems(prev =>
      prev.some(d => d.id === id) ? prev : [...prev, { kind, id, label }]
    );
    if (kind === 'table') {
      setEditedTables(prev => { const n = { ...prev }; delete n[id]; return n; });
    } else if (kind === 'graph') {
      setEditedGraphs(prev => { const n = { ...prev }; delete n[id]; return n; });
    } else if (kind === 'image') {
      setEditedImages(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }, []);

  const isDeleted = useCallback(
    (id) => deletedItems.some(d => d.id === id),
    [deletedItems]
  );

  const inPlaceGraphMap = useMemo(() => {
    const map = {};
    if (!shouldRenderFullResponse) return map;
    const deletedIds = new Set([
      ...deletedItems.filter(d => d.kind === 'ipgraph').map(d => d.id),
      ...(agent?.editedResponse?.deleted_in_place_graph_ids || []),
    ]);
    const persistedById = (agent?.editedResponse?.graphs || []).reduce((acc, g) => {
      if (g?.graph_id) acc[g.graph_id] = g;
      return acc;
    }, {});
    (agent?.response?.in_place_graphs || []).forEach(ipg => {
      if (ipg?.index == null) return;
      if (ipg.in_place_graph_id && deletedIds.has(ipg.in_place_graph_id)) return;
      const persisted = persistedById[ipg.graph_id] || {};
      const persistedOverride = {
        ...('subtitle' in persisted && { subtitle: persisted.subtitle }),
        ...('title' in persisted && { title: persisted.title }),
      };
      const liveOverride = editedInPlaceGraphs[ipg.graph_id] || {};
      map[ipg.index] = { ...ipg, ...persistedOverride, ...liveOverride };
    });
    return map;
  }, [shouldRenderFullResponse, agent?.response?.in_place_graphs, agent?.editedResponse?.graphs, agent?.editedResponse?.deleted_in_place_graph_ids, editedInPlaceGraphs, deletedItems]);

  const persistedInPlaceImagesById = useMemo(() => {
    const m = {};
    (agent?.editedResponse?.in_place_images || []).forEach(ipi => {
      if (ipi?.in_place_image_id) m[ipi.in_place_image_id] = ipi;
    });
    return m;
  }, [agent?.editedResponse?.in_place_images]);

  const inPlaceImageMap = useMemo(() => {
    const map = {};
    if (!shouldRenderFullResponse) return map;
    const deletedIds = new Set([
      ...deletedItems.filter(d => d.kind === 'ipimage').map(d => d.id),
      ...(agent?.editedResponse?.deleted_in_place_image_ids || []),
    ]);
    (agent?.response?.in_place_images || []).forEach(ipi => {
      if (ipi?.index == null) return;
      if (ipi.in_place_image_id && deletedIds.has(ipi.in_place_image_id)) return;
      
      const persisted = persistedInPlaceImagesById[ipi.in_place_image_id] || {};
      const live = editedInPlaceImages[ipi.in_place_image_id] || {};
      const overrides = {
        ...('subtitle' in persisted && { subtitle: persisted.subtitle }),
        ...('footer' in persisted && { footer: persisted.footer }),
        ...live,
      };
      map[ipi.index] = { ...ipi, ...overrides };
    });
    return map;
  }, [shouldRenderFullResponse, agent?.response?.in_place_images, agent?.editedResponse?.deleted_in_place_image_ids, persistedInPlaceImagesById, editedInPlaceImages, deletedItems]);


  const deletedIpiIndices = useMemo(() => {
    const deletedIds = new Set([
      ...deletedItems.filter(d => d.kind === 'ipimage').map(d => d.id),
      ...(agent?.editedResponse?.deleted_in_place_image_ids || []),
    ]);
    const idxSet = new Set();
    if (deletedIds.size === 0) return idxSet;
    (agent?.response?.in_place_images || []).forEach(ipi => {
      if (ipi?.index != null && ipi.in_place_image_id && deletedIds.has(ipi.in_place_image_id)) {
        idxSet.add(String(ipi.index));
      }
    });
    return idxSet;
  }, [deletedItems, agent?.editedResponse?.deleted_in_place_image_ids, agent?.response?.in_place_images]);

  
  const deletedIpgIndices = useMemo(() => {
    const deletedIds = new Set([
      ...deletedItems.filter(d => d.kind === 'ipgraph').map(d => d.id),
      ...(agent?.editedResponse?.deleted_in_place_graph_ids || []),
    ]);
    const idxSet = new Set();
    if (deletedIds.size === 0) return idxSet;
    (agent?.response?.in_place_graphs || []).forEach(ipg => {
      if (ipg?.index != null && ipg.in_place_graph_id && deletedIds.has(ipg.in_place_graph_id)) {
        idxSet.add(String(ipg.index));
      }
    });
    return idxSet;
  }, [deletedItems, agent?.editedResponse?.deleted_in_place_graph_ids, agent?.response?.in_place_graphs]);

  const patchInPlaceImageEditsInHtml = useCallback((html) => {
    if (!html) return html;
    const hasFigure = html.indexOf('genbi-inplace-figure') !== -1;
    const hasPortal = html.indexOf('genbi-inplace-graph-portal') !== -1 || html.indexOf('data-ipg-root') !== -1;
    if (!hasFigure && !hasPortal) return html;

    
    const effectiveOverride = (id) => {
      const persisted = persistedInPlaceImagesById[id] || {};
      const live = editedInPlaceImages[id] || {};
      const o = {};
      if ('subtitle' in persisted) o.subtitle = persisted.subtitle;
      if ('footer' in persisted) o.footer = persisted.footer;
      if ('subtitle' in live) o.subtitle = live.subtitle;
      if ('footer' in live) o.footer = live.footer;
      return o;
    };

    const edited = Object.values(inPlaceImageMap).filter(ipi => {
      if (ipi.index == null) return false;
      const o = effectiveOverride(ipi.in_place_image_id);
      return 'subtitle' in o || 'footer' in o;
    });
    // Proceed if there are caption/footer edits OR deletions (image or graph) to strip.
    if (edited.length === 0 && deletedIpiIndices.size === 0 && deletedIpgIndices.size === 0) {
      return html;
    }

    const doc = new DOMParser().parseFromString(`<div id="__ipiroot">${html}</div>`, 'text/html');
    const root = doc.getElementById('__ipiroot');
    if (!root) return html;

    let changed = false;

    const rowSpan = (row) => Array.from(row.children)
      .filter(c => /^(td|th)$/i.test(c.tagName))
      .reduce((sum, c) => sum + (parseInt(c.getAttribute('colspan'), 10) || 1), 0);

   
    const cellCount = (row) => Array.from(row.children)
      .filter(c => /^(td|th)$/i.test(c.tagName)).length;

    const stripFigureCell = (el) => {
      const cell = el.closest('td, th');
      if (!cell) { el.remove(); return; }
      const cellText = (cell.textContent || '').trim();
      const elText = (el.textContent || '').trim();
      // Any media/figure/portal in the cell that isn't the element being removed.
      const otherMedia = Array.from(
        cell.querySelectorAll('img, table, .genbi-inplace-figure, [data-ipg-index], [data-ipg-root]')
      ).some(m => m !== el && !el.contains(m));
      const figureOnly = cellText === elText && !otherMedia;
      if (!figureOnly) { el.remove(); return; }

      const tr = cell.closest('tr');
      const tbl = cell.closest('table');

      // Uniform data grid? → keep the cell (just remove the figure) so the other
      // columns don't shift.
      if (tr && tbl) {
        const myCount = cellCount(tr);
        const sharing = Array.from(tbl.querySelectorAll('tr'))
          .filter(r => cellCount(r) === myCount).length;
        if (sharing >= 2) { el.remove(); return; }
      }

      // Layout table → remove the whole cell + reflow the row.
      const tableCols = tbl ? Math.max(0, ...Array.from(tbl.querySelectorAll('tr')).map(rowSpan)) : 0;
      cell.remove();
      if (tr) {
        const remaining = Array.from(tr.children).filter(c => /^(td|th)$/i.test(c.tagName));
        if (remaining.length === 0) {
          tr.remove();
        } else if (tableCols && rowSpan(tr) < tableCols) {
          const last = remaining[remaining.length - 1];
          const cur = parseInt(last.getAttribute('colspan'), 10) || 1;
          last.setAttribute('colspan', String(cur + (tableCols - rowSpan(tr))));
        }
      }
    };

    // ── Strip deleted in-place image figures ──
    deletedIpiIndices.forEach(idx => {
      const fig = root.querySelector(`.genbi-inplace-figure[data-ipi-index="${idx}"]`);
      if (fig) { stripFigureCell(fig); changed = true; }
    });

    // ── Strip deleted in-place graph portals (same cell behaviour as images) ──
    deletedIpgIndices.forEach(idx => {
      const portal =
        root.querySelector(`.genbi-inplace-graph-portal[data-ipg-index="${idx}"]`) ||
        root.querySelector(`[data-ipg-root="${idx}"]`);
      if (portal) { stripFigureCell(portal); changed = true; }
    });

    // ── Apply caption/footer edits to surviving image figures ──
    edited.forEach(ipi => {
      const fig = root.querySelector(`.genbi-inplace-figure[data-ipi-index="${ipi.index}"]`);
      if (!fig) return;
      const overrides = effectiveOverride(ipi.in_place_image_id);
      const figNum = ipi.img_num ?? ipi.index;

      if ('subtitle' in overrides) {
        const sub = overrides.subtitle || '';
        let cap = fig.querySelector('.genbi-inplace-caption');
        if (sub) {
          if (!cap) {
            cap = doc.createElement('div');
            cap.className = 'genbi-inplace-caption ev-fs-11 text-center mt-1';
            fig.insertBefore(cap, fig.querySelector('img.genbi-inplace-img') || fig.firstChild);
          }
          cap.innerHTML = `<b>Figure ${figNum}.</b> ${sub}`;
        } else if (cap) {
          cap.remove();
        }
        changed = true;
      }

      if ('footer' in overrides) {
        const foot = overrides.footer || '';
        let ft = fig.querySelector('.genbi-inplace-footer');
        if (foot) {
          if (!ft) {
            ft = doc.createElement('div');
            ft.className = 'genbi-inplace-footer ev-fs-11 ev-small-p-dark mt-1';
            fig.appendChild(ft);
          }
          ft.innerHTML = `<i class="bi bi-pencil-sticky me-1"></i><b>Note</b> ${foot}`;
        } else if (ft) {
          ft.remove();
        }
        changed = true;
      }
    });

    return changed ? root.innerHTML : html;
  }, [inPlaceImageMap, editedInPlaceImages, persistedInPlaceImagesById, deletedIpiIndices, deletedIpgIndices]);

  
  const collapseInPlaceFiguresToMarkers = useCallback((html) => {
    if (!html) return html;
    if (
      html.indexOf('genbi-inplace-graph-portal') === -1 &&
      html.indexOf('genbi-inplace-figure') === -1 &&
      html.indexOf('data-ipg-root') === -1
    ) {
      return html;
    }
    const doc = new DOMParser().parseFromString(`<div id="__ipmroot">${html}</div>`, 'text/html');
    const root = doc.getElementById('__ipmroot');
    if (!root) return html;
    let changed = false;


    root.querySelectorAll('.genbi-inplace-graph-portal[data-ipg-index], [data-ipg-root]').forEach(el => {
      const n = el.getAttribute('data-ipg-index') ?? el.getAttribute('data-ipg-root');
      if (n == null || n === '') return;
      el.replaceWith(doc.createTextNode(`[[InPlace_Graph${n}]]`));
      changed = true;
    });

   
    root.querySelectorAll('.genbi-inplace-figure[data-ipi-index]').forEach(el => {
      const m = el.getAttribute('data-ipi-index');
      if (m == null || m === '') return;
      el.replaceWith(doc.createTextNode(`[[InPlace_Image${m}]]`));
      changed = true;
    });

    return changed ? root.innerHTML : html;
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!onSaveResponse) return;

    const restoredMarkdown = restoreCitations(editedMarkdown, citationMap);

    const deletedTableIds = deletedItems.filter(d => d.kind === 'table').map(d => d.id);
    const deletedGraphIds = deletedItems.filter(d => d.kind === 'graph').map(d => d.id);
    const deletedImageIds = deletedItems.filter(d => d.kind === 'image').map(d => d.id);
    const deletedInPlaceImageIds = deletedItems.filter(d => d.kind === 'ipimage').map(d => d.id);
    const deletedInPlaceGraphIds = deletedItems.filter(d => d.kind === 'ipgraph').map(d => d.id);
    const deletedSet = new Set(deletedItems.map(d => d.id));

    const tablesPayload = Object.entries(editedTables)
      .filter(([tableId, data]) => {
        if (deletedSet.has(tableId)) return false;
        const orig = originalTablesRef.current[tableId] || {};
        return data.html !== orig.html || data.footnote !== orig.footnote;
      })
      .map(([tableId, data]) => ({
        table_id: tableId,
        ...(data.html !== undefined && {
          html: restoreCitations(collapseInPlaceFiguresToMarkers(data.html), citationMap),
        }),
        ...(data.footnote !== undefined && { footnote: restoreCitations(data.footnote, citationMap) }),
      }));

    const graphsPayload = Object.entries(editedGraphs)
      .filter(([graphId, v]) => !deletedSet.has(graphId) && (v.subtitle !== undefined || v.title !== undefined))
      .map(([graphId, data]) => ({
        graph_id: graphId,
        ...(data.subtitle !== undefined && { subtitle: restoreCitations(data.subtitle, citationMap) }),
        ...(data.title !== undefined && { title: restoreCitations(data.title, citationMap) }),
      }));

    const imagesPayload = Object.entries(editedImages)
      .filter(([imageId, v]) => !deletedSet.has(imageId) && (v.subtitle !== undefined || v.footer !== undefined))
      .map(([imageId, data]) => ({
        image_id: imageId,
        ...(data.subtitle !== undefined && { subtitle: restoreCitations(data.subtitle, citationMap) }),
        ...(data.footer !== undefined && { footer: restoreCitations(data.footer, citationMap) }),
      }));

    const inPlaceGraphsPayload = Object.entries(editedInPlaceGraphs)
      .filter(([, v]) => v.subtitle !== undefined || v.title !== undefined)
      .map(([graphId, data]) => ({
        graph_id: graphId,
        in_place: true,
        ...(data.subtitle !== undefined && { subtitle: restoreCitations(data.subtitle, citationMap) }),
        ...(data.title    !== undefined && { title: data.title }),
      }));

    const ipiIdByIndex = {};
    (agent?.response?.in_place_images || []).forEach(ipi => {
      if (ipi?.index != null && ipi.in_place_image_id) ipiIdByIndex[String(ipi.index)] = ipi.in_place_image_id;
    });
    const capturedInPlaceImages = {};
    Object.values(editedTables).forEach(({ html }) => {
      if (!html || html.indexOf('genbi-inplace-figure') === -1) return;
      const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
      doc.querySelectorAll('.genbi-inplace-figure[data-ipi-index]').forEach(fig => {
        const id = ipiIdByIndex[String(fig.getAttribute('data-ipi-index'))];
        if (!id) return;
        const capEl = fig.querySelector('.genbi-inplace-caption');
        const ftEl = fig.querySelector('.genbi-inplace-footer');
        const entry = {};
        if (capEl) {
          const c = capEl.cloneNode(true);
          const b = c.querySelector('b'); if (b) b.remove();   // drop "Figure N." prefix
          entry.subtitle = c.innerHTML.trim();
        }
        if (ftEl) {
          const f = ftEl.cloneNode(true);
          f.querySelectorAll('i, b').forEach(n => n.remove());  // drop icon + "Note"
          entry.footer = f.innerHTML.trim();
        }
        if ('subtitle' in entry || 'footer' in entry) capturedInPlaceImages[id] = entry;
      });
    });
    
    const mergedInPlaceImages = {};
    (agent?.editedResponse?.in_place_images || []).forEach(ipi => {
      if (!ipi?.in_place_image_id) return;
      mergedInPlaceImages[ipi.in_place_image_id] = {
        ...('subtitle' in ipi && { subtitle: ipi.subtitle }),
        ...('footer' in ipi && { footer: ipi.footer }),
      };
    });
    Object.entries(capturedInPlaceImages).forEach(([id, v]) => {
      mergedInPlaceImages[id] = { ...mergedInPlaceImages[id], ...v };
    });
    Object.entries(editedInPlaceImages).forEach(([id, v]) => {
      mergedInPlaceImages[id] = { ...mergedInPlaceImages[id], ...v };
    });

    const inPlaceImagesPayload = Object.entries(mergedInPlaceImages)
      .filter(([ipiId, v]) => !deletedSet.has(ipiId) && (v.subtitle !== undefined || v.footer !== undefined))
      .map(([ipiId, data]) => ({
        in_place_image_id: ipiId,
        ...(data.subtitle !== undefined && { subtitle: restoreCitations(data.subtitle, citationMap) }),
        ...(data.footer   !== undefined && { footer: restoreCitations(data.footer, citationMap) }),
      }));

    onSaveResponse(agent.id, {
      markdown: restoredMarkdown,
      tables: tablesPayload,
      graphs: [...graphsPayload, ...inPlaceGraphsPayload],
      images: imagesPayload,
      in_place_images: inPlaceImagesPayload,
      ...(deletedTableIds.length > 0 && { deleted_table_ids: deletedTableIds }),
      ...(deletedGraphIds.length > 0 && { deleted_graph_ids: deletedGraphIds }),
      ...(deletedImageIds.length > 0 && { deleted_image_ids: deletedImageIds }),
      ...(deletedInPlaceImageIds.length > 0 && { deleted_in_place_image_ids: deletedInPlaceImageIds }),
      ...(deletedInPlaceGraphIds.length > 0 && { deleted_in_place_graph_ids: deletedInPlaceGraphIds }),
    });

    setDeletedItems([]);
    setEditedInPlaceGraphs({});
    setEditedInPlaceImages({});
    setIsEditing(false);
    setCitationMap({});
  }, [agent.id, agent?.response?.in_place_images, agent?.editedResponse?.in_place_images, editedMarkdown, editedTables, editedGraphs, editedImages, editedInPlaceGraphs, editedInPlaceImages, deletedItems, citationMap, onSaveResponse, collapseInPlaceFiguresToMarkers]);

  const danglingDeletedRefs = useMemo(() => {
    if (deletedItems.length === 0) return [];
    const refMap = referenceIndex?.idToReferencingAgents || {};
    return deletedItems
      .map(d => {
        const localCount = editedMarkdown
          ? (editedMarkdown.match(
              new RegExp(`\\[${d.id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g')
            ) || []).length
          : 0;
        // Other agents (exclude the current one) that reference this id.
        const otherAgents = (refMap[d.id] || []).filter(a => a.agentId !== agent?.id);
        return { ...d, count: localCount, otherAgents };
      })
      .filter(d => d.count > 0 || d.otherAgents.length > 0);
  }, [deletedItems, editedMarkdown, referenceIndex, agent?.id]);

  const sourceMarkdown = agent?.editedResponse?.markdown ?? agent?.response?.markdown ?? "";

  const editedTablesOverrideMap = (agent?.editedResponse?.tables || []).reduce((acc, t) => {
    if (t?.table_id) acc[t.table_id] = { html: t.html, footnote: t.footnote };
    return acc;
  }, {});

  const editedGraphsOverrideMap = (agent?.editedResponse?.graphs || []).reduce((acc, g) => {
    if (g?.graph_id) acc[g.graph_id] = g;
    return acc;
  }, {});

  const editedImagesOverrideMap = (agent?.editedResponse?.images || []).reduce((acc, img) => {
    if (img?.image_id) acc[img.image_id] = img;
    return acc;
  }, {});

  // ---------- FIGURE & TABLE NUMBERING LOGIC ----------
  let graphFigures = [];
  let imageFigures = [];
  let tableItems = [];
  const graphIndexToFigure = {};
  const imageIndexToFigure = {};
  const tableIndexToNumber = {};

  if (shouldRenderFullResponse) {
    const graphs = agent.response.graphs || [];
    const images = agent.response.images || [];
    const tables = agent.response.tables || [];

    graphFigures = graphs.map((graph, idx) => {
      const figureNumber = graph.graph_number ?? (idx + 1);
      const override = editedGraphsOverrideMap[graph.graph_id] || {};
      graphIndexToFigure[idx + 1] = figureNumber;
      return {
        ...graph,
        ...('subtitle' in override && { subtitle: override.subtitle }),
        ...('title' in override && { title: override.title }),
        figureNumber,
      };
    });

    imageFigures = images.map((image, idx) => {
      const figureNumber = image.img_num ?? (idx + 1);
      const override = editedImagesOverrideMap[image.image_id] || {};
      imageIndexToFigure[idx + 1] = figureNumber;
      return {
        ...image,
        ...('subtitle' in override && { subtitle: override.subtitle }),
        ...('footer' in override && { footer: override.footer }),
        figureNumber,
      };
    });

    tableItems = tables.map((table, idx) => {
      const tableNumber = table.table_number ?? (idx + 1);
      const override = editedTablesOverrideMap[table.table_id] || {};
      tableIndexToNumber[idx + 1] = tableNumber;
      return {
        ...table,
        html: override.html ?? table.html,
        ...('footnote' in override && { footnote: override.footnote }),
        tableNumber
      };
    });
  }
  // ---------------------------------------------------

  const processMarkdownForDisplay = (markdownText) => {
    if (!shouldRenderFullResponse || !markdownText) return markdownText || '';

    let displayMarkdown = markdownText;

    const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const figureById = {};
    const tableById = {};
    // Start from the template-wide index so references to OTHER agents' tables/figures
    // also resolve (e.g. this response cites [agent_4032_table_1_...] owned by agent 4032).
    Object.entries(referenceIndex?.idToNumber || {}).forEach(([id, info]) => {
      if (info?.number == null) return;
      if (info.kind === 'table') tableById[id] = info.number;
      else figureById[id] = info.number;
    });
    // Current agent's own outputs take precedence (freshest numbering).
    graphFigures.forEach(g => { if (g.graph_id) figureById[g.graph_id] = g.figureNumber; });
    imageFigures.forEach(im => { if (im.image_id) figureById[im.image_id] = im.figureNumber; });
    tableItems.forEach(t => { if (t.table_id) tableById[t.table_id] = t.tableNumber; });

    Object.entries(figureById).forEach(([id, num]) => {
      displayMarkdown = displayMarkdown
        .replace(new RegExp(`\\(\\[${escapeRe(id)}\\]\\)`, 'g'), `(Figure ${num})`)
        .replace(new RegExp(`\\[${escapeRe(id)}\\]`, 'g'), `Figure ${num}`);
    });
    Object.entries(tableById).forEach(([id, num]) => {
      displayMarkdown = displayMarkdown
        .replace(new RegExp(`\\(\\[${escapeRe(id)}\\]\\)`, 'g'), `(Table ${num})`)
        .replace(new RegExp(`\\[${escapeRe(id)}\\]`, 'g'), `Table ${num}`);
    });

    const refRegex = /\[(graph|image|table)_(\d+)\]/g;

    displayMarkdown = displayMarkdown.replace(
      refRegex,
      (match, type, indexStr) => {
        const index = Number(indexStr);
        if (type === "graph") {
          const figNum = graphIndexToFigure[index];
          return figNum ? `Figure ${figNum}` : match;
        }
        if (type === "image") {
          const figNum = imageIndexToFigure[index];
          return figNum ? `Figure ${figNum}` : match;
        }
        if (type === "table") {
          const tblNum = tableIndexToNumber[index];
          return tblNum ? `Table ${tblNum}` : match;
        }
        return match;
      }
    );

    const truncate = (str, max = 15) => {
      const s = (str || "").trim();
      if (s.length <= max) return s;
      return s.slice(0, Math.max(0, max - 1)) + "…";
    };

    const trimDocTagSpans = (md, maxLen = 15) => {
      if (!md) return md;
      return md.replace(
        /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([\s\S]*?)<\/span>/g,
        (full, attrs, inner) => {
          const rawText = (inner || "").trim();
          if (rawText.includes("<")) return full;
          const shortText = truncate(rawText, maxLen);
          const hasTitle = /\stitle=/.test(attrs);
          const safeTitle = rawText.replace(/"/g, "&quot;");
          const newAttrs = hasTitle ? attrs : `${attrs} title="${safeTitle}"`;
          return `<span${newAttrs}>${shortText}</span>`;
        }
      );
    };
    displayMarkdown = trimDocTagSpans(displayMarkdown, 15);

    // Render :::quotation ... ::: fenced blocks as styled HTML divs.
    // borderColor + textColor from formatJson prop (template format_json) if available,
    // else canonical default #00B0F0.
    const quotationBorderColor = formatJson?.quotation?.borderColor || "#00B0F0";
    const quotationTextColor   = formatJson?.quotation?.color       || quotationBorderColor;
    displayMarkdown = displayMarkdown.replace(
      /^:::quotation\s*\n([\s\S]*?)\n?:::/gm,
      (_, content) =>
        `<div style="border-top:1.5px solid ${quotationBorderColor};border-bottom:1.5px solid ${quotationBorderColor};text-align:center;color:${quotationTextColor};font-style:italic;padding:4px 0;margin:8px 0;">${content.trim()}</div>`
    );

    return displayMarkdown;
  };

  // ---------- CROSS-REFERENCE PLACEHOLDERS IN MARKDOWN ----------
  let tableItemsForView = tableItems;

  // Table html now arrives with reference chips already applied by the backend, so the token
  // pass below is only a fallback for html that still holds tokens (an unsaved editor edit).
  // TableComponent injects this html directly, and trimDocTagSpans only touches card6/card7,
  // so the card9/card10 chips reach the DOM with their classes intact.
  if (shouldRenderFullResponse && tableItems.length > 0) {
    tableItemsForView = tableItems.map(table => ({
      ...table,
      html: table.html
        ? patchInPlaceImageEditsInHtml(processMarkdownForDisplay(table.html))
        : table.html
    }));
  }

  // An artifact cited here but owned by another agent is NOT rendered here: it renders
  // under its owner, at its placeholder there or below that agent's text, and stays a chip
  // in every agent that merely cites it. This used to hand the renderer those borrowed
  // artifacts, so a table owned by a later agent appeared twice.

  return (
    <div>
      {shouldRenderFullResponse ? (
        <div>
          <div className={`d-flex justify-content-between align-items-center mb-2 ${compact ? 'mt-1' : 'mt-3'}`}>
            <div className="d-flex align-items-center">
              <label className="ev-small-p-blue ev-bold-med">
                &nbsp; Agent Response
              </label>

              {/* {shouldRenderFullResponse && (
                <span
                  className={`ev-btn-xsmall ms-1 ev-bold-reg ${isManualResponse ? 'genbi-source-manual' : 'genbi-source-ai'}`}
                  data-tooltip={isManualResponse ? 'This version was edited manually' : 'This version was generated by AI'}
                >
                  {isManualResponse ? (
                    <><i className="bi bi-person-fill me-1"></i>Manual</>
                  ) : (
                    <><i className="bi bi-stars"></i>AI</>
                  )}
                </span>
              )} */}

              {hasWarnings && (
                <div
                  className="ev-alert-card-warning px-2 py-0 d-flex align-items-center  ev-radius-small ms-1 avatar-tooltip"
                  onClick={() => setIsWarningsModalOpen(true)}
                  data-tooltip="Click to see warnings"
                  role="button"
                  tabIndex={0}
                  aria-label="Click to see warnings"
                  style={{ cursor: 'pointer' }}
                >
                  <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                </div>
              )}

              {!isEditing && agent.response.agent_id && (
                <div className="d-flex align-items-center gap-1 ms-1">
                  <div className="position-relative">        
                    <div className="ev-message-card1 ev-btn-xsmall d-flex align-items-center pe-1 ev-radius-large prompt-translate-pill">
                      <i className="bi bi-globe2 me-1"></i>
                      <span>{agent.response.language_name || "English"}</span>
                      {hasLangMismatch && (
                        <i
                          className="bi bi-exclamation-triangle-fill text-warning ms-1 avatar-tooltip"
                          data-tooltip="Prompt & response languages are out of sync"
                        ></i>
                      )}
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-xsmall ms-2 avatar-tooltip-bottom"
                        onClick={handleTranslateClick}
                        disabled={isTranslateDisabled}
                        data-tooltip="Translate response"
                      >
                        <i className="bi bi-translate"></i> Translate
                      </button>
                    </div>

                    {isTranslatePopupOpen && (
                      <AgentTranslationModal
                        currentLanguageId={agent.response.language_id || 1}
                        otherSideLangId={promptLangId}
                        otherSideLabel="Prompt"
                        onClose={handleCloseTranslatePopup}
                        onTranslate={handleTranslate}
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-xsmall avatar-tooltip-bottom ev-bold-reg"
                    onClick={() => setIsLogsModalOpen(true)}
                    data-tooltip="View execution logs"
                  >
                    <i className="bi bi-journal-text "></i> Logs
                  </button>
                  {responseHeaderNotice && responseHeaderNotice}
                </div>
              )}

              {isEditing && (
                <span className="ev-btn-xsmall ms-1 genbi-editing-pill ev-bold-reg">
                  <i className="bi bi-pencil-fill me-1"></i>Editing
                </span>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              {!isEditing && (
                <>
                  {headerExtra && headerExtra}
                  {agent.response.execution_metadata?.timestamp && (
                    <span
                      className="ev-message-card3 ev-btn-xsmall d-flex align-items-center avatar-tooltip"
                      data-tooltip={formatDateTime(agent.response.execution_metadata.timestamp)}
                    >
                      <i className="bi bi-clock"></i>
                    </span>
                  )}
                  {hasPendingEdits && (
                    <span
                      className="ev-btn-xsmall genbi-edited-badge avatar-tooltip"
                      data-tooltip="Unsaved edits — click Save Template to persist"
                    >
                      <i className="bi bi-circle-fill me-1"></i>Edited
                    </span>
                  )}
                  {onSaveResponse && (
                    <button
                      type="button"
                      className="ev-btn-lightblue-fill-round ev-btn-xsmall avatar-tooltip"
                      onClick={handleEditClick}
                      data-tooltip="Edit response"
                    >
                      <i className="bi bi-pencil-square me-1"></i>Edit
                    </button>
                  )}
                </>
              )}

              {isEditing && (
                <>
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small"
                    onClick={handleCancelEdit}
                  >
                  Cancel
                  </button>
                  <button
                    type="button"
                    className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                    onClick={handleSaveEdit}
                  >
                  Save Changes
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={`ev-card-2 p-2 ev-radius-small ${isEditing ? 'genbi-response-card-editing' : ''}`}>

            {isEditing ? (
              <div className="genbi-response-edit-wrapper" data-color-mode="light">
                {danglingDeletedRefs.length > 0 && (
                  <div className="ev-alert-card-warning mb-3">
                    <p className="mb-1">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      &nbsp;
                      <strong>Deleted item(s) are still referenced in the text.</strong>
                      &nbsp;Remove these references from the response above, or they will
                      show as broken links after saving:
                    </p>
                    <ul className="mb-0">
                      {danglingDeletedRefs.map(d => (
                        <li key={d.id} className="ev-small-p-blue">
                          {d.label}
                          {d.count > 0 && ` — referenced ${d.count} ${d.count === 1 ? 'time' : 'times'} in this response`}
                          {d.otherAgents.length > 0 && (
                            <>
                              {d.count > 0 ? '; also' : ' — referenced'} in {d.otherAgents.map(a => a.agentLabel).join(', ')}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <AgentResponseEditor
                    value={editedMarkdown}
                    onChange={setEditedMarkdown}
                    docs={agentDocs}
                    refs={agentRefs}
                    onNeedReferences={fetchAvailableReferences}
                    onCreateReference={createManualReference}
                    refsLoading={refsLoading}
                  />
                </div>

                {tableItems.length > 0 && (
                  <div className="mb-3">
                    {tableItems.filter(table => !isDeleted(table.table_id)).map((table) => (
                      <div
                        key={table.table_id || table.tableNumber}
                        className="genbi-deletable-item position-relative mb-2"
                        onClick={e => {
                          const figure = e.target.closest('[data-ipi-index]');
                          if (figure) {
                            e.stopPropagation();
                            const ipiIndex = parseInt(figure.dataset.ipiIndex, 10);
                            const ipi = isNaN(ipiIndex) ? null : inPlaceImageMap[ipiIndex];
                            if (ipi) {
                              const overrides = editedInPlaceImages[ipi.in_place_image_id] || {};
                              setEditingInPlaceImage({
                                ipi,
                                localSubtitle: overrides.subtitle ?? ipi.subtitle ?? '',
                                localFooter:   overrides.footer   ?? ipi.footer   ?? '',
                              });
                            }
                          }
                        }}
                      >
                        <div className="d-flex justify-content-end mb-1">
                          <button
                            type="button"
                            className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg text-danger"
                            onClick={() => handleDeleteItem('table', table.table_id, `Table ${table.tableNumber}`)}
                            data-tooltip="Delete this table"
                          >
                            <i className="bi bi-trash me-1"></i>Delete Table {table.tableNumber}
                          </button>
                        </div>
                        <TableComponent
                          tableData={{ ...table, html: patchInPlaceImageEditsInHtml(editedTables[table.table_id]?.html ?? table.html) }}
                          isLoading={false}
                          contentId={table.tableNumber}
                          subtitle={table.subtitle}
                          footer={'footnote' in (editedTables[table.table_id] || {}) ? editedTables[table.table_id].footnote : (table.footnote ?? null)}
                          isEditing={true}
                          onFieldChange={(field, value) => handleTableFieldChange(table.table_id, field, value)}
                          inPlaceGraphMap={inPlaceGraphMap}
                          inPlaceImageMap={inPlaceImageMap}
                          onInPlaceGraphChange={handleInPlaceGraphChange}
                          onInPlaceGraphEdit={(state) => setEditingInPlaceGraph(state)}
                          renderTableTools={({ borderColor, onBorderColor }) => (
                            <span
                              className="d-inline-flex align-items-center gap-1 me-2 genbi-tblborder-shell"
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <ColorPickerButton
                                value={borderColor || '#000000'}
                                onChange={(c) => onBorderColor(c)}
                                iconOnly
                                title="Table border colour"
                                icon={
                                  <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall">
                                    Table border
                                  </button>
                                }
                              />
                            </span>
                          )}
                          renderFormatPill={(api) => (
                            <RichFormatPill
                              visible={!!api.rect}
                              anchorRect={api.rect}
                              clipEl={api.clipEl}
                              mode="cell"
                              marks={api.marks}
                              cellState={api.cellState}
                              onToggle={api.onToggle}
                              onColor={api.onColor}
                              onHighlight={api.onHighlight}
                              onCellBg={api.onCellBg}
                              onAlign={api.onAlign}
                              onRemove={api.onRemove}
                            />
                          )}
                          docs={agentDocs}
                          refs={agentRefs}
                          onNeedReferences={fetchAvailableReferences}
                          onCreateReference={createManualReference}
                          refsLoading={refsLoading}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {graphFigures.length > 0 && (
                  <div className="mb-3">
                    {graphFigures.filter(graph => !isDeleted(graph.graph_id)).map((graph) => {
                      const gOverrides = editedGraphs[graph.graph_id] || {};
                      const currentTitle = graph.figure_dict?.layout?.title;
                      const titleText = typeof currentTitle === 'string' ? currentTitle : (currentTitle?.text ?? '');
                      return (
                        <div key={graph.graph_id || graph.figureNumber} className="genbi-deletable-item position-relative mb-2">
                          <div className="d-flex justify-content-end mb-1">
                            <button
                              type="button"
                              className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg text-danger"
                              onClick={() => handleDeleteItem('graph', graph.graph_id, `Figure ${graph.figureNumber}`)}
                              data-tooltip="Delete this figure"
                            >
                              <i className="bi bi-trash me-1"></i>Delete Figure {graph.figureNumber}
                            </button>
                          </div>
                          <GraphComponent
                            graphData={graph}
                            isLoading={false}
                            contentId={graph.figureNumber}
                            subtitle={'subtitle' in gOverrides ? gOverrides.subtitle : graph.subtitle}
                            footer={"footer" in graph ? graph.footer : null}
                            isEditing={true}
                            editedTitle={'title' in gOverrides ? gOverrides.title : titleText}
                            onFieldChange={(field, value) => handleGraphFieldChange(graph.graph_id, field, value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {imageFigures.length > 0 && (
                  <div className="mb-3">
                    {imageFigures.filter(image => !isDeleted(image.image_id)).map((image) => {
                      const iOverrides = editedImages[image.image_id] || {};
                      return (
                        <div key={image.image_id || image.figureNumber} className="genbi-deletable-item position-relative mb-2">
                          <div className="d-flex justify-content-end mb-1">
                            <button
                              type="button"
                              className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg text-danger"
                              onClick={() => handleDeleteItem('image', image.image_id, `Figure ${image.figureNumber}`)}
                              data-tooltip="Delete this figure"
                            >
                              <i className="bi bi-trash me-1"></i>Delete Figure {image.figureNumber}
                            </button>
                          </div>
                          <ImageComponent
                            imageData={image}
                            isLoading={false}
                            contentId={image.figureNumber}
                            subtitle={'subtitle' in iOverrides ? iOverrides.subtitle : image.subtitle}
                            footer={'footer' in iOverrides ? iOverrides.footer : ("footer" in image ? image.footer : null)}
                            isEditing={true}
                            onFieldChange={(field, value) => handleImageFieldChange(image.image_id, field, value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {agent.response.references &&
                  ((Array.isArray(agent.response.references) &&
                    agent.response.references.length > 0) ||
                    (typeof agent.response.references === "object" &&
                      !Array.isArray(agent.response.references) &&
                      ((agent.response.references.doc_ids &&
                        agent.response.references.doc_ids.length > 0) ||
                        (agent.response.references.ar_ids &&
                          agent.response.references.ar_ids.length > 0)))) && (
                    <div data-genbi-no-feedback-select="true">
                      <ReferencesComponent references={agent.response.references} />
                    </div>
                  )}
              </div>
            ) : (
              <>
            <InlineArtifactRenderer
              markdown={sourceMarkdown}
              tables={tableItemsForView}
              graphs={graphFigures}
              images={imageFigures}
              markdownComponents={markdownComponents}
              processMarkdown={processMarkdownForDisplay}
              inPlaceGraphMap={inPlaceGraphMap}
              inPlaceImageMap={inPlaceImageMap}
              onInPlaceGraphView={(ipg) => setViewingInPlaceGraph(ipg)}
              onTableClick={e => {
                const figure = e.target.closest('[data-ipi-index]');
                const img    = figure ? figure.querySelector('img') : e.target.closest('td img, th img');
                if (img && img.src) {
                  const ipiIndex = figure ? parseInt(figure.dataset.ipiIndex, 10) : null;
                  const ipi      = ipiIndex != null ? inPlaceImageMap[ipiIndex] : null;
                  let figNum = ipi?.img_num ?? '';
                  if (!figNum && figure) {
                    const b = figure.querySelector('.genbi-inplace-caption b');
                    const m = (b?.textContent || '').match(/Figure\s+(\S+)\.?/i);
                    if (m) figNum = m[1];
                  }
                  const subtitle = ipi?.subtitle || figure?.dataset?.subtitle || img.alt || '';
                  let footer = ipi?.footer || '';
                  if (!footer && figure) {
                    const fd = figure.querySelector('.genbi-inplace-footer');
                    if (fd) footer = fd.textContent?.replace(/^\s*Note\s*/i, '').trim() || '';
                  }
                  setInPlaceLightbox({ url: img.src, subtitle, footer, figNum });
                }
              }}
            />

            {agent.response.references &&
              ((Array.isArray(agent.response.references) &&
                agent.response.references.length > 0) ||
                (typeof agent.response.references === "object" &&
                  !Array.isArray(agent.response.references) &&
                  ((agent.response.references.doc_ids &&
                    agent.response.references.doc_ids.length > 0) ||
                    (agent.response.references.ar_ids &&
                      agent.response.references.ar_ids.length > 0)))) && (
                <div data-genbi-no-feedback-select="true">
                  <ReferencesComponent references={agent.response.references} />
                </div>
              )}
          </>
            )}

          {inPlaceLightbox && createPortal((
            <div
              className="genbi-inplace-lightbox-overlay"
              onClick={() => setInPlaceLightbox(null)}
            >
              <div
                className="genbi-inplace-lightbox-card"
                onClick={e => e.stopPropagation()}
              >
                <button type="button" className="ev-btn-text-only-fill ev-fs-11 align-self-end"
                  onClick={() => setInPlaceLightbox(null)}>
                  <i className="bi bi-x-lg"></i>
                </button>
                {inPlaceLightbox.subtitle && (
                  <div className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap text-center">
                    <i className="bi bi-image me-1"></i>
                    <b>Figure {inPlaceLightbox.figNum}.</b> {inPlaceLightbox.subtitle}
                  </div>
                )}
                <img src={inPlaceLightbox.url} alt={inPlaceLightbox.subtitle || ''}
                  className="genbi-inplace-lightbox-img" />
                {inPlaceLightbox.footer && (
                  <div className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
                    <i className="bi bi-pencil-square me-1"></i>
                    <b>Note</b> {inPlaceLightbox.footer}
                  </div>
                )}
              </div>
            </div>
          ), document.body)}

          {viewingInPlaceGraph && createPortal((
            <div className="genbi-inplace-lightbox-overlay"
              onClick={() => setViewingInPlaceGraph(null)}>
              <div className="genbi-inplace-graph-view-card"
                onClick={e => e.stopPropagation()}>
                <div className="d-flex justify-content-end">
                  <button type="button" className="ev-btn-text-only-fill ev-fs-11"
                    onClick={() => setViewingInPlaceGraph(null)}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <div className="genbi-inplace-graph-view-body">
                  <GraphComponent
                    graphData={viewingInPlaceGraph}
                    isLoading={false}
                    contentId={viewingInPlaceGraph.img_num ?? viewingInPlaceGraph.index}
                    subtitle={viewingInPlaceGraph.subtitle}
                    footer={viewingInPlaceGraph.footer || null}
                  />
                </div>
              </div>
            </div>
          ), document.body)}

          {editingInPlaceGraph && createPortal((
            <div
              className="genbi-inplace-modal-overlay"
              onClick={() => setEditingInPlaceGraph(null)}
            >
              <div
                className="genbi-inplace-edit-card"
                onClick={e => e.stopPropagation()}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="ev-small-p-blue ev-bold-med">
                    <i className="bi bi-bar-chart-fill me-1"></i>
                    Figure {editingInPlaceGraph.ipg.img_num ?? editingInPlaceGraph.ipg.index}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg text-danger"
                      onClick={() => {
                        handleDeleteInPlaceGraph(editingInPlaceGraph.ipg.in_place_graph_id);
                        setEditingInPlaceGraph(null);
                      }}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Delete Figure
                    </button>
                    <button type="button" className="ev-btn-text-only-fill ev-fs-11"
                      onClick={() => setEditingInPlaceGraph(null)}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>

                <div className="genbi-figure-edit-field">
                  <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                    <i className="bi bi-bar-chart-fill"></i>{' '}
                    <b>Figure {editingInPlaceGraph.ipg.img_num ?? editingInPlaceGraph.ipg.index} — Subtitle</b>
                  </span>
                  <textarea
                    ref={ipGraphSubtitleRef}
                    className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                    value={editingInPlaceGraph.localSubtitle}
                    onChange={e => setEditingInPlaceGraph(g => ({ ...g, localSubtitle: e.target.value }))}
                    rows={2}
                    placeholder="Edit subtitle..."
                  />
                  <MentionAutocomplete
                    textareaRef={ipGraphSubtitleRef}
                    docs={agentDocs}
                    refs={agentRefs}
                    onNeedReferences={fetchAvailableReferences}
                    onCreateReference={createManualReference}
                    refsLoading={refsLoading}
                    onInsert={(v, caret) => {
                      setEditingInPlaceGraph(g => ({ ...g, localSubtitle: v }));
                      window.requestAnimationFrame(() => {
                        const ta = ipGraphSubtitleRef.current;
                        if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
                      });
                    }}
                  />
                </div>

                <div className="genbi-figure-edit-field">
                  <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                    <i className="bi bi-type-h1"></i> <b>Chart Title</b>
                  </span>
                  <input
                    type="text"
                    className="formField2 ev-fs-11 w-100"
                    value={editingInPlaceGraph.localTitle}
                    onChange={e => setEditingInPlaceGraph(g => ({ ...g, localTitle: e.target.value }))}
                    placeholder="Edit chart title..."
                  />
                </div>

                <div className="genbi-inplace-graph-preview">
                  <GraphComponent
                    graphData={{
                      ...editingInPlaceGraph.ipg,
                      subtitle: editingInPlaceGraph.localSubtitle,
                    }}
                    isLoading={false}
                    contentId={
                      editingInPlaceGraph.ipg.img_num ??
                      editingInPlaceGraph.ipg.index
                    }
                    subtitle={editingInPlaceGraph.localSubtitle}
                    footer={editingInPlaceGraph.ipg.footer || null}
                    isInPlace={true}
                    editedTitle={editingInPlaceGraph.localTitle}
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small"
                    onClick={() => setEditingInPlaceGraph(null)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                    onClick={() => {
                      handleInPlaceGraphChange(
                        editingInPlaceGraph.ipg.graph_id,
                        {
                          subtitle: editingInPlaceGraph.localSubtitle,
                          title: editingInPlaceGraph.localTitle,
                        }
                      );
                      setEditingInPlaceGraph(null);
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          ), document.body)}

          {editingInPlaceImage && createPortal((
            <div
              className="genbi-inplace-modal-overlay"
              onClick={() => setEditingInPlaceImage(null)}
            >
              <div
                className="genbi-inplace-edit-card genbi-inplace-edit-card--img"
                onClick={e => e.stopPropagation()}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="ev-small-p-blue ev-bold-med">
                    <i className="bi bi-image me-1"></i>
                    Figure {editingInPlaceImage.ipi.img_num ?? editingInPlaceImage.ipi.index}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <button type="button"
                      className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg text-danger"
                      onClick={() => {
                        handleDeleteInPlaceImage(editingInPlaceImage.ipi.in_place_image_id);
                        setEditingInPlaceImage(null);
                      }}>
                      <i className="bi bi-trash me-1"></i>Delete Figure
                    </button>
                    <button type="button" className="ev-btn-text-only-fill ev-fs-11"
                      onClick={() => setEditingInPlaceImage(null)}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>

                <div className="genbi-figure-edit-field">
                  <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                    <i className="bi bi-image"></i>{' '}
                    <b>Figure {editingInPlaceImage.ipi.img_num ?? editingInPlaceImage.ipi.index} — Subtitle</b>
                  </span>
                  <textarea className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                    ref={ipImageSubtitleRef}
                    value={editingInPlaceImage.localSubtitle}
                    onChange={e => setEditingInPlaceImage(g => ({ ...g, localSubtitle: e.target.value }))}
                    rows={2} placeholder="Edit subtitle..." />
                  <MentionAutocomplete
                    textareaRef={ipImageSubtitleRef}
                    docs={agentDocs}
                    refs={agentRefs}
                    onNeedReferences={fetchAvailableReferences}
                    onCreateReference={createManualReference}
                    refsLoading={refsLoading}
                    onInsert={(v, caret) => {
                      setEditingInPlaceImage(g => ({ ...g, localSubtitle: v }));
                      window.requestAnimationFrame(() => {
                        const ta = ipImageSubtitleRef.current;
                        if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
                      });
                    }}
                  />
                </div>

                <div className="genbi-inplace-img-preview">
                  <img
                    src={editingInPlaceImage.ipi.presigned_url || ''}
                    alt={editingInPlaceImage.localSubtitle || ''}
                    className="genbi-inplace-img-preview-img"
                  />
                </div>

                <div className="genbi-figure-edit-field">
                  <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                    <i className="bi bi-pencil-square"></i> <b>Note</b>
                  </span>
                  <textarea className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                    ref={ipImageFooterRef}
                    value={editingInPlaceImage.localFooter}
                    onChange={e => setEditingInPlaceImage(g => ({ ...g, localFooter: e.target.value }))}
                    rows={2} placeholder="Edit note..." />
                  <MentionAutocomplete
                    textareaRef={ipImageFooterRef}
                    docs={agentDocs}
                    refs={agentRefs}
                    onNeedReferences={fetchAvailableReferences}
                    onCreateReference={createManualReference}
                    refsLoading={refsLoading}
                    onInsert={(v, caret) => {
                      setEditingInPlaceImage(g => ({ ...g, localFooter: v }));
                      window.requestAnimationFrame(() => {
                        const ta = ipImageFooterRef.current;
                        if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
                      });
                    }}
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small"
                    onClick={() => setEditingInPlaceImage(null)}>Cancel</button>
                  <button type="button" className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                    onClick={() => {
                      if (editingInPlaceImage.ipi.in_place_image_id) {
                        handleInPlaceImageChange(editingInPlaceImage.ipi.in_place_image_id, {
                          subtitle: editingInPlaceImage.localSubtitle,
                          footer:   editingInPlaceImage.localFooter,
                        });
                      }
                      setEditingInPlaceImage(null);
                    }}>
                    <i className="bi bi-check2 me-1"></i>Apply
                  </button>
                </div>
              </div>
            </div>
          ), document.body)}

          </div>
        </div>
      ) : (
        <div>
          {status === "RUNNING" ? (
            <>
              <label className="ev-small-p-blue mb-2 mt-3 ev-bold-med">
                &nbsp; Agent Response
              </label>
              <div className="ev-card-4 vh-20 border-0 ev-radius-small">
                <div className="generating-loader-wrapper">
                  <span className="generating-loader-letter">G</span>
                  <span className="generating-loader-letter">e</span>
                  <span className="generating-loader-letter">n</span>
                  <span className="generating-loader-letter">e</span>
                  <span className="generating-loader-letter">r</span>
                  <span className="generating-loader-letter">a</span>
                  <span className="generating-loader-letter">t</span>
                  <span className="generating-loader-letter">i</span>
                  <span className="generating-loader-letter">n</span>
                  <span className="generating-loader-letter">g</span>
                  <div className="generating-loader"></div>
                </div>

                <div className="content-loader-card">
                  <div className="content-loader-card__skeleton content-loader-card__title w-70"></div>
                  <div className="content-loader-card__skeleton content-loader-card__title"></div>
                  <div className="content-loader-card__skeleton content-loader-card__description"></div>
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      )}
      {isLogsModalOpen && (
        <AgentLogModal
          agentId={agent.id}
          fabricId={fabricId}
          version={latestVersion}
          retrievalConfidence={retrievalConfidence}
          agentName={agent.agent_name}
          onClose={() => setIsLogsModalOpen(false)}
          authToken={authToken}
        />
      )}
      {isWarningsModalOpen && (
        <AgentWarningsModal
          sensitiveFlags={sensitiveFlags}
          groundingFlags={groundingFlags}
          boundsEnforcement={boundsEnforcement}
          agentName={agent.agent_name}
          onClose={() => setIsWarningsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default GenbiResponseComponent;
